import { useEffect, useState } from "react";
import { ChevronDown, Pencil } from "lucide-react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Tonal } from "@/components/ui/tonal";
import { Kicker } from "@/components/ui/kicker";
import { VoiceProse } from "@/components/ui/voice-prose";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useCanEdit } from "@/lib/project-access";
import { cn } from "@/lib/utils";

interface BriefBlockProps {
  scenarioId: string;
  brief: string | null;
  personaName: string;
  firstName: string;
}

export function BriefBlock({
  scenarioId,
  brief,
  personaName,
  firstName,
}: BriefBlockProps) {
  const utils = trpc.useUtils();
  const [briefOpen, setBriefOpen] = useState(!brief);
  const [editingBrief, setEditingBrief] = useState(false);
  const canEdit = useCanEdit();

  const setBriefMutation = trpc.scenario.setBrief.useMutation({
    onSuccess: () => {
      utils.scenario.getById.invalidate({ id: scenarioId });
      setEditingBrief(false);
      toast.success("Brief saved");
    },
    onError: (err) => toastTrpcError(err, "Failed to save brief"),
  });

  return (
    <section className="space-y-3">
      <Collapsible open={briefOpen} onOpenChange={setBriefOpen}>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-2 cursor-pointer"
              aria-label={briefOpen ? "Collapse brief" : "Expand brief"}
            >
              <Kicker>Brief</Kicker>
              <span className="text-sm text-muted-foreground">
                First-person rendering for {firstName}
              </span>
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  "h-3.5 w-3.5 text-muted-foreground transition-transform",
                  briefOpen && "rotate-180",
                )}
              />
            </button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="mt-3">
          {brief ? (
            <div className="space-y-4">
              <VoiceProse className="max-w-[70ch]">{brief}</VoiceProse>
              {canEdit && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border/60 pt-4">
                  <button
                    type="button"
                    onClick={() => setEditingBrief(true)}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline cursor-pointer"
                  >
                    <Pencil className="h-3 w-3" aria-hidden="true" />
                    Edit brief
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Tonal padding="md" className="max-w-[62ch] space-y-3">
              <p className="text-sm text-muted-foreground">
                No brief yet — the walkthrough needs a first-person rendering of
                the situation before {firstName} can run it.
              </p>
              {canEdit && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => setEditingBrief(true)}
                  >
                    <Pencil className="mr-1.5 h-3 w-3" aria-hidden="true" />
                    Write brief
                  </Button>
                </div>
              )}
            </Tonal>
          )}
        </CollapsibleContent>
      </Collapsible>

      {canEdit && (
        <EditBriefDialog
          open={editingBrief}
          onOpenChange={setEditingBrief}
          personaName={personaName}
          brief={brief}
          isSaving={setBriefMutation.isPending}
          onSave={(newBrief) =>
            setBriefMutation.mutate({ id: scenarioId, brief: newBrief })
          }
        />
      )}
    </section>
  );
}

interface EditBriefDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personaName: string;
  brief: string | null;
  isSaving: boolean;
  onSave: (brief: string) => void;
}

function EditBriefDialog({
  open,
  onOpenChange,
  personaName,
  brief,
  isSaving,
  onSave,
}: EditBriefDialogProps) {
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (open) setDraft(brief ?? "");
  }, [open, brief]);
  const firstName = personaName.split(" ")[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {brief ? `Edit ${firstName}'s brief` : `Write ${firstName}'s brief`}
          </DialogTitle>
          <DialogDescription>
            The brief is what the walkthrough actually runs — a first-person
            rendering of the situation as {firstName} experiences it.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          aria-label="First-person brief"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={10}
          className="text-base leading-relaxed"
          placeholder={`Write as ${firstName}, in first person — what brought you here and what's on your mind right now.`}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave(draft.trim())}
            disabled={isSaving || !draft.trim()}
          >
            {isSaving ? "Saving…" : "Save brief"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
